import { useCallback, useEffect, useState } from 'react';
import { fetchWithCorpId } from './api';
import useDebouncedValue from './useDebouncedValue';

// Shared server-side table engine: owns the page/size/sort/filter state, the
// reset-to-first-page effect, the fetch effect (with a stale-response guard),
// and the URLSearchParams builder used by both the fetch and the Excel export.
//
// Every list endpoint is a Spring `Page<DTO>` returning { content,
// totalElements, totalPages }, driven by the same query params: page (0-based),
// size, sort (logical field), direction (asc|desc), plus one same-named param
// per filter key. Filter values are strings, except multi-select columns whose
// value is an array (each element is appended as a repeated param).
//
// Usage:
//   const t = useServerTable('/clients', {
//     emptyFilter: EMPTY_FILTER,
//     initialSort: { key: 'id', direction: 'asc' },
//   });
// then read t.rows / t.loading / t.error / t.page / t.totalPages / ... and wire
// t.handleSort, t.handleFilterChange, t.clearFilters, t.setPage, t.reload.
export default function useServerTable(endpoint, options = {}) {
  const {
    emptyFilter = {},
    initialSort = { key: 'id', direction: 'asc' },
    initialSize = 10,
    debounceMs = 300,
  } = options;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState(emptyFilter);
  const debouncedFilter = useDebouncedValue(filter, debounceMs);
  const [sortConfig, setSortConfig] = useState(initialSort);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(initialSize);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [reloadKey, setReloadKey] = useState(0);

  // Build the query string from the (debounced) filter, current sort, and the
  // given page/size. Array-valued filters append one param per element; empty
  // strings/nulls are omitted so absent filters never reach the backend.
  const buildParams = useCallback((pageArg, sizeArg) => {
    const params = new URLSearchParams();
    params.set('page', pageArg);
    params.set('size', sizeArg);
    params.set('sort', sortConfig.key);
    params.set('direction', sortConfig.direction);
    Object.keys(debouncedFilter).forEach(key => {
      const value = debouncedFilter[key];
      if (Array.isArray(value)) {
        value.forEach(item => { if (item !== '' && item != null) params.append(key, item); });
      } else if (value !== '' && value != null) {
        params.set(key, value);
      }
    });
    return params;
  }, [debouncedFilter, sortConfig]);

  // Any change to filter, size, or sort returns to the first page so the user
  // is never stranded on a page that no longer exists under the new query.
  useEffect(() => { setPage(0); }, [debouncedFilter, size, sortConfig]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchWithCorpId(`${endpoint}?${buildParams(page, size).toString()}`)
      .then(data => {
        if (cancelled) return;
        setRows(data?.content ?? []);
        setTotalElements(data?.totalElements ?? 0);
        setTotalPages(data?.totalPages ?? 0);
        setError(null);
      })
      .catch(e => { if (!cancelled) { setError(e.message || 'Failed to load'); setRows([]); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [endpoint, page, size, buildParams, reloadKey]);

  const handleSort = useCallback((key) => setSortConfig(prev => ({
    key,
    direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
  })), []);

  const handleFilterChange = useCallback((e) => {
    const { name, value } = e.target;
    setFilter(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleChangePageSize = useCallback((e) => setSize(parseInt(e.target.value, 10)), []);
  const clearFilters = useCallback(() => setFilter(emptyFilter), [emptyFilter]);
  const reload = useCallback(() => setReloadKey(k => k + 1), []);

  const hasActiveFilters = Object.keys(emptyFilter).some(key => {
    const value = filter[key];
    return Array.isArray(value) ? value.length > 0 : Boolean(value);
  });

  // Fetch every matching row in one request for Excel export, reusing the live
  // sort and filter. Returns the plain content array.
  const fetchAll = useCallback(async () => {
    const data = await fetchWithCorpId(`${endpoint}?${buildParams(0, 100000).toString()}`);
    return data?.content ?? [];
  }, [endpoint, buildParams]);

  return {
    rows, loading, error,
    filter, setFilter, handleFilterChange, clearFilters, hasActiveFilters,
    sortConfig, handleSort,
    page, setPage, size, handleChangePageSize,
    totalElements, totalPages,
    reload, buildParams, fetchAll,
  };
}
